import React from 'react';
import Button from '../../Utils/Button/Button';
import ProgressBar from '../../Utils/ProgressBar/ProgressBar';
import { ProgressSegmentData } from '../../Utils/ProgressBar/ProgressSegmentData';
import styles from '../../Evaluate/Card/Card.module.css';
import { ProductType } from '../../../Types/ProductType';
import { formatUkDateSafe } from '../../../Helpers/Dates';

type ObjectiveProgress = {
  label: string;
  percentage?: number;
  segments?: ProgressSegmentData[];
  total?: number;
};

type ObjectiveCardProps = {
  areaId: string;
  title: string;
  subtitle: string;
  progress: ObjectiveProgress[];
  archived?: boolean;
  onArchive?: () => void;
  onExport?: () => void;
  onComplete?: () => void;
  canArchive? : boolean;
  canExport ? : boolean;
  canView ? : boolean;
  hideExportButton?: boolean;
  dueDate?: Date;
  roles?: string[];
  groups?: string[];
  organisations?: string[];
  departments?: string[];
  participantCount?: number;
  productType?: number;
  status?: string;
  impactSegments?: Array<{ label: string; value: number; colour: string }>;
};

const ObjectiveCard = ({
  areaId,
  title,
  subtitle,
  progress,
  archived,
  onArchive,
  onExport,
  onComplete,
  canArchive,
  canExport,
  canView,
  hideExportButton,
  dueDate,
  roles,
  groups,
  organisations,
  departments,
  participantCount,
  productType,
  status,
  impactSegments,
}: ObjectiveCardProps) => {
  // Get the primary impact (segment with highest value, or first with value > 0)
  const getPrimaryImpact = () => {
    if (!impactSegments || impactSegments.length === 0) return null;
    const segmentsWithValues = impactSegments.filter(seg => seg.value > 0);
    if (segmentsWithValues.length === 0) return null;
    // Sort by value descending and get the first one
    return segmentsWithValues.sort((a, b) => b.value - a.value)[0];
  };

  const primaryImpact = getPrimaryImpact();

  return (
    <div className={styles.container}>
      <div className={styles.objectiveHeader}>
        <div className={styles.titleColumn}>
          <h5>{title}</h5>
        </div>
        <div className={`${styles.progressMetrics} ${styles.progressColumn}`}>
          {progress.map(({ label, percentage, segments, total }, index) => (
            <div key={label} className={styles.progressMetric}>
              <div className={styles.progressMetricHeader}>
                <span>{label}</span>
              </div>
              {segments && segments.length > 0 ? (
                <ProgressBar segments={segments} />
              ) : percentage !== undefined ? (
                <ProgressBar percentage={percentage} />
              ) : (
                <div className={styles.progressSegmentsPlaceholder} />
              )}
            </div>
          ))}
        </div>
        <div className={`${styles.footerRight} ${styles.objectiveActions}`}>
          <Button
            size="Small"
            colour="#FFF"
            textColour="#000"
            border="rgba(0,0,0,0.2)"
            clickHandler={onArchive}
            disabled={!canArchive}
          >
            {archived ? 'Unarchive' : 'Archive'}
          </Button>
          {(onExport || !hideExportButton) && (
            <Button
              size="Small"
              colour="#FFF"
              textColour="#000"
              border="rgba(0,0,0,0.2)"
              clickHandler={onExport}
              disabled={!canExport}
            >
              Export
            </Button>
          )}
          {archived ? '' : <Button
            size="Small"
            colour="#FFF"
            textColour="#000"
            border="rgba(0,0,0,0.2)"
            clickHandler={onComplete}
            style={{ minWidth: '100px' }}
            disabled={!canView}
          >
            Complete Plan
          </Button>}
        </div>
      </div>

      <div className={styles.infoContainer}>
        <div className={styles.info}>
          {roles && roles.length > 0 && (
            <p>Roles: {roles.join(", ")}</p>
          )}
          
          {dueDate && <p>Due: {formatUkDateSafe(dueDate)}</p>}
          
          {status && (
            <p>Status: {status}</p>
          )}
          
          {primaryImpact && (
            <p>Impact: {primaryImpact.label}</p>
          )}
        
          {groups && groups.length > 0 && (
            <p>Group: {groups.join(", ")}</p>
          )}
              
          {organisations && organisations.length > 0 && (
            <p>Organisation: {organisations.join(", ")}</p>
          )}
          {departments && departments.length > 0 && (
            <p>Department: {departments.join(", ")}</p>
          )}

          {participantCount !== undefined && (
            <p className={styles.participants}>{participantCount} {participantCount === 1 ? 'Participant' : 'Participants'}</p>
          )}

          {productType !== undefined && (
            <p>Type: {productType != null ? ProductType[productType as ProductType] : '—'}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObjectiveCard;
